import { useIntl } from '@capsicum/common';
import { type ObjectDataShape, type PartnerWithImageMap, PostTypes, usePartners } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { match } from 'ts-pattern';

import { feedDataAtom, type FeedDataAtomType } from '../../data-access';

export type UseTokensNeededShape = {
	amountNeeded: number;
	isTypeformPostType?: boolean;
	/** First ID recieved will act as the "mainPartner" taking preference on the outcome of the results. */
	mainPartnerID: string;
	/** data object from the activity. */
	postData: ObjectDataShape;
	postType?: null | PostTypes;
	restPartnerIDs?: Array<string>;
};

export type PartnerTokenData = {
	balance: number;
	canParticipate: boolean;
	currencyId: string;
	participation: 'ALLOWED' | 'NOT_ENOUGH_OWNED' | 'NOT_ENOUGH_STAKED';
	partnerId: string;
	shortCode: string;
	staked: number;
	symbol: string;
	voteTokenId: string;
};

export const useTokensNeeded = ({
	amountNeeded = 0,
	mainPartnerID,
	postData = {},
	postType = null,
	restPartnerIDs = []
}: UseTokensNeededShape) => {
	const { $t } = useIntl();
	const { arePartnersLoading, partners } = usePartners();
	const { getOperationDetailsFromPartner, isFeedAtomDataLoading, participationRequirement, walletsBalance } =
		useAtomValue(feedDataAtom);

	const allPartnersIDs: Array<string> = useMemo(
		() => [mainPartnerID, ...(restPartnerIDs ?? [])],
		[mainPartnerID, restPartnerIDs]
	);

	const requiredPartners = useMemo(
		() =>
			partners
				?.filter(partner => partner.id && allPartnersIDs.includes(partner.id))
				.reduce<
					Record<string, PartnerWithImageMap>
				>((acc, partner) => ({ [partner.id!]: partner, ...acc }), {}) ?? {},
		[partners]
	);

	const postTypesAvailableForStake = useMemo(
		() => [PostTypes.BO_FORM, PostTypes.GAMIFICATION_FORM, PostTypes.WP_IMAGE_TYPE_TYPEFORM],
		[]
	);

	amountNeeded = Number(amountNeeded);
	const isMultipleAmountNeeded = amountNeeded > 1;
	const isTokenRequiredEnabled = postData?.tokenRequired;
	const isLoading = arePartnersLoading || isFeedAtomDataLoading;

	const partnersTokenData: Array<PartnerTokenData> = useMemo(
		() =>
			allPartnersIDs.map(partnerID => {
				const partner = requiredPartners[partnerID];
				const partnerWalletBalance = walletsBalance?.getCurrencyBy(
					currency => currency.currency.id === partner?.currencyId
				);

				const operation = getOperationDetailsFromPartner(partner?.currencyId);
				const balance = Number(partnerWalletBalance?.totalAmount) || 0;
				const staked = Number(operation?.user?.currentStake) || 0;
				const hasCurrentStake = operation?.user?.hasCurrentStake || false;

				const participation = match<
					{
						enoughOwnedTokens: boolean;
						enoughStakedTokens: boolean;
						participationRequirement: typeof participationRequirement;
					},
					PartnerTokenData['participation']
				>({
					enoughOwnedTokens: balance >= amountNeeded,
					enoughStakedTokens: hasCurrentStake && staked >= amountNeeded,
					participationRequirement
				})
					.with(
						{
							enoughOwnedTokens: false,
							participationRequirement: 'OWNED'
						},
						() => 'NOT_ENOUGH_OWNED'
					)
					.with(
						{
							enoughOwnedTokens: false,
							participationRequirement: 'STAKED'
						},
						() => 'NOT_ENOUGH_OWNED'
					)
					.with(
						{
							enoughStakedTokens: false,
							participationRequirement: 'STAKED'
						},
						() =>
							postTypesAvailableForStake.includes(postType as unknown as PostTypes)
								? 'NOT_ENOUGH_STAKED'
								: 'ALLOWED'
					)
					.otherwise(() => 'ALLOWED');

				return {
					balance,
					canParticipate: participation === 'ALLOWED',
					currencyId: partner?.currencyId || '',
					participation,
					partnerId: partner?.id || '',
					shortCode: operation?.currency?.shortCode || partner?.tokenSymbol?.replace(/\$/g, '') || '',
					staked,
					symbol: operation?.currency?.symbol || partner?.tokenSymbol || '',
					voteTokenId: partner?.tokenId || ''
				};
			}),
		[
			requiredPartners,
			amountNeeded,
			walletsBalance,
			participationRequirement,
			allPartnersIDs,
			getOperationDetailsFromPartner
		]
	);

	const mainPartner = partnersTokenData[0]!;

	const isTotalBalanceMissingFromAllPartners = useMemo(
		() => partnersTokenData.every(data => !data.canParticipate),
		[partnersTokenData]
	);

	const firstPartnerWithBalance = useMemo(
		() => partnersTokenData.find(partner => partner.canParticipate) ?? mainPartner,
		[mainPartner, partnersTokenData]
	);

	const info = useMemo(
		() =>
			match<
				{
					participation: PartnerTokenData['participation'];
					participationRequirement: FeedDataAtomType['participationRequirement'];
				},
				{
					cta: string;
					details: string;
					type: 'OWN' | 'STAKE';
				}
			>({
				participation: mainPartner.participation,
				participationRequirement
			})
				.with({ participation: 'NOT_ENOUGH_STAKED' }, () => ({
					cta: $t(isMultipleAmountNeeded ? 'feed.chip.stakeFanTokens' : 'feed.chip.stakeFanToken'),
					details: $t(isMultipleAmountNeeded ? 'feed.chip.stakedNeededPlural' : 'feed.chip.stakedNeeded', {
						amount: amountNeeded
					}),
					type: 'STAKE'
				}))
				.with(
					{
						participationRequirement: 'STAKED'
					},
					() => ({
						cta: $t(isMultipleAmountNeeded ? 'feed.chip.buyFanTokens' : 'feed.chip.buyFanToken'),
						details: $t(isMultipleAmountNeeded ? 'feed.chip.ownedNeededPlural' : 'feed.chip.ownedNeeded', {
							amount: amountNeeded
						}),
						type: 'OWN'
					})
				)
				.otherwise(() => ({
					cta: $t(isMultipleAmountNeeded ? 'feed.chip.buyFanTokens' : 'feed.chip.buyFanToken'),
					details: $t(isMultipleAmountNeeded ? 'feed.chip.amountNeededPlural' : 'feed.chip.amountNeeded', {
						amount: amountNeeded,
						partner: mainPartner.shortCode
					}),
					type: 'OWN'
				})),
		[
			$t,
			amountNeeded,
			mainPartner.shortCode,
			mainPartner.participation,
			isMultipleAmountNeeded,
			participationRequirement
		]
	);

	return {
		firstPartnerWithBalance,
		info,
		isLoading,
		isMultipleAmountNeeded,
		isTokenRequiredEnabled,
		isTotalBalanceMissingFromAllPartners,
		mainPartner,
		partnersTokenData
	};
};
